import Vec2 from 'vec2';

import {
  BACKSPLASH_INPUT_LIMIT,
  MAX_RADIUS_SAGITTA,
  SIDE_ITEM_OPTIONS,
  WATERFALL_INPUT_LIMIT,
} from '~/core/constants';
import {
  ShapeType,
  SideItemOption,
  isBumpOut,
  isRadiusOut,
} from '~/core/enums';
import {
  getOutlineCustomSideEdges,
  getOutlinePointsFromModifiedShape,
  isPointInsidePolygon,
  roundToArrowLengthGranularity,
} from '~/core/helpers';
import {
  ApplianceType,
  AppliancesAllowedOnShapes,
  AppliancesAllowedOutsideShapes,
  ArrowInfo,
  DrawingShape,
} from '~/core/models';
import { clampWithLimit } from '~/core/utils';

export const updateEdgeRadiusSagitta = (
  arrow: ArrowInfo,
  shape: DrawingShape
) => {
  if (!arrow.size) {
    return;
  }

  getOutlineCustomSideEdges(shape)
    .filter((edge) => edge !== undefined)
    .filter(
      (edge) =>
        isRadiusOut(edge) &&
        edge.radiusSagitta &&
        edge.id === arrow.drawingEdgeId
    )
    .forEach((edge) => {
      edge.radiusSagitta = Math.min(arrow.size as number, MAX_RADIUS_SAGITTA);
    });
};

export const updateEdgeBumpOutDepth = (arrow: ArrowInfo, shape: DrawingShape) =>
  getOutlineCustomSideEdges(shape)
    .filter((edge) => !!edge)
    .filter(
      (edge) =>
        isBumpOut(edge) &&
        edge.bumpOut?.depth &&
        edge.id === arrow.drawingEdgeId
    )
    .forEach((edge) => {
      if (edge.bumpOut) {
        edge.bumpOut.depth = arrow.size as number;
      }
    });

export const updateSideItemDepth = (arrow: ArrowInfo, shape: DrawingShape) => {
  const edge = shape.drawingEdges.find(
    ({ id, sideItem }) =>
      id === arrow.drawingEdgeId &&
      sideItem.depth &&
      sideItem.type &&
      SIDE_ITEM_OPTIONS.includes(sideItem.type) // TODO EZE-1448
  );
  const removedEdge = shape.removedElementsHistory?.edgesAndCorners?.find(
    (removed) => removed.drawingEdge.id === edge?.id
  );

  if (!edge || !removedEdge) {
    return;
  }

  const inputLimit = {
    [SideItemOption.WATERFALL]: WATERFALL_INPUT_LIMIT,
    [SideItemOption.BACKSPLASH]: BACKSPLASH_INPUT_LIMIT,
  };
  const adjustedSize = clampWithLimit(
    roundToArrowLengthGranularity(arrow.size as number),
    inputLimit[edge.sideItem.type as SideItemOption]
  );

  edge.sideItem.depth = adjustedSize;
  edge.sideItem.arrowsInfo.forEach((sideItemArrow) => {
    if (sideItemArrow.isSideItemPresent) {
      sideItemArrow.size = adjustedSize;
    }
  });
  removedEdge.drawingEdge = edge;
};

export const isShapeValidForApplianceInside =
  (applianceType: ApplianceType, centerPoint: Vec2) => (shape: DrawingShape) =>
    AppliancesAllowedOnShapes.includes(applianceType) &&
    shape.type !== ShapeType.FAUX_SHAPE &&
    isPointInsidePolygon(getOutlinePointsFromModifiedShape(shape), centerPoint);

export const isShapeValidForApplianceOutside =
  (applianceType: ApplianceType) => (shape: DrawingShape) =>
    AppliancesAllowedOutsideShapes.includes(applianceType) &&
    shape.type === ShapeType.FAUX_SHAPE;
